import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import ShadowWrapper from '../ShadowWrapper/ShadowWrapper.jsx';
import NavigationMenu from './Menus/NavigationMenu.jsx';
import SearchModal from '../SearchModal/SearchModal.jsx';
import MyAccountMenu from './Menus/MyAccount.jsx';
import MySiteMenu from './Menus/MySite.jsx';
import HeaderNotification from '../HeaderNotification/HeaderNotification.jsx';
import styles from './SharedHeader.styles.css';
import { getSubdomain } from '../../lib/utils.js';
import * as iconData from './icons.json';
import { fetchHeaderData } from '../../lib/shared-header-utils.js';
import AuthProvider from './authProvider.js';

const DEFAULT_AVATAR_IMAGE = 'https://crossroads-media.imgix.net/images/avatar.svg';

const HIDDEN_BADGE_IDS = ['warp-speed', 'navigator', 'revived-from-cryo-sleep', 'magi'];

const BUILD_BADGES_ENABLED = process.env.NEXT_PUBLIC_10X_BADGES_ENABLED === 'true';

const SharedHeader = () => {
  const [userState, setUserState] = useState({
    isLoading: true,
    isAuthenticated: false,
    user: null
  });
  const [serviceTimes, setServiceTimes] = useState([]);
  const [happenings, setHappenings] = useState([]);
  const [navState, setNavState] = useState({
    openNavName: '',
    activeMenu: '',
    nestedContent: null,
    activeNavItem: ''
  });
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [newBadgeCount, setNewBadgeCount] = useState(0);

  // Runtime override (persisted via localStorage). Initialize from localStorage (read-only).
  const [runtimeBadgesEnabled, setRuntimeBadgesEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('badges') === 'true';
    } catch {
      return false;
    }
  });

  const badgesEnabled = BUILD_BADGES_ENABLED || runtimeBadgesEnabled;

  const hasNewBadges = badgesEnabled && newBadgeCount > 0;

  const dropdownRefs = useRef({
    'my-site-nav': null,
    'give-nav': null,
    'profile-nav': null
  });
  const headerContainerRef = useRef(null);

  const rootURL = `https://${getSubdomain(process.env.CRDS_ENV)}.crossroads.net`;

  const updateHeaderHeight = () => {
    if (headerContainerRef.current) {
      const height = headerContainerRef.current.getBoundingClientRect().height;
      if (height > 0) {
        setHeaderHeight(height);
      }
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = params.get('badges');

      if (qp === 'true' || qp === '1') {
        localStorage.setItem('badges', 'true');
        setRuntimeBadgesEnabled(true);
      } else if (qp === 'false' || qp === '0' || qp === 'off') {
        localStorage.removeItem('badges');
        setRuntimeBadgesEnabled(false);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const fetchHeaderDataAsync = async () => {
      try {
        const formattedEnv = process.env.CRDS_ENV === 'production' ? 'prod' : process.env.CRDS_ENV;
        const data = await fetchHeaderData(
          `https://crds-data.crossroads.net/shared-header/${formattedEnv}.json`
        );
        setHeaderData(data);
      } catch (error) {
        console.error('Error fetching header data:', error);
      }
    };

    fetchHeaderDataAsync();
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        await AuthProvider.initialize();
        setUserState({
          isLoading: false,
          isAuthenticated: AuthProvider.getIsAuthenticated(),
          user: AuthProvider.getUser()
        });
      } catch (error) {
        console.error('Error initializing authentication:', error);
        setUserState({ isLoading: false, isAuthenticated: false, user: null });
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    if (!badgesEnabled) {
      setNewBadgeCount(0);
      return;
    }

    if (userState.user?.user?.Id) {
      fetch(
        `${process.env.NEXT_PUBLIC_CRDS_SITE_DOMAIN}api/badges/get?userId=${userState.user.user.Id}`
      )
        .then((r) => r.json())
        .then((badges) => {
          const count = Object.entries(badges).filter(
            ([id, b]) => b.isNew === true && !HIDDEN_BADGE_IDS.includes(id)
          ).length;
          setNewBadgeCount(count);
        })
        .catch((error) => {
          console.error('Error fetching badges:', error);
        });
    }
  }, [userState.user, badgesEnabled]);

  useEffect(() => {
    if (!badgesEnabled) return;

    const handleBadgeDismissed = (e) => {
      if (String(e.detail?.userId) !== String(userState?.user?.user?.Id)) return;
      if (HIDDEN_BADGE_IDS.includes(e.detail?.badgeId)) return;
      setNewBadgeCount((c) => Math.max(0, c - 1));
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('badgeStatusUpdated', handleBadgeDismissed);
      return () => window.removeEventListener('badgeStatusUpdated', handleBadgeDismissed);
    }
  }, [userState.user, badgesEnabled]);

  useEffect(() => {
    if (userState.user) {
      fetchLocation(userState.user.campus.Name);
    }
  }, [userState.user]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!navState.openNavName) return;

      const openMenuRef = dropdownRefs.current[navState.openNavName];

      if (openMenuRef) {
        const path = event.composedPath && event.composedPath();
        if (path && !path.includes(openMenuRef)) {
          toggleMenu(navState.openNavName);
        }
      }
    };

    if (navState.openNavName) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      if (navState.openNavName) {
        document.removeEventListener('mousedown', handleOutsideClick);
      }
    };
  }, [navState.openNavName]);

  useEffect(() => {
    if (!headerContainerRef.current) return;

    const initialMeasurement = () => {
      requestAnimationFrame(() => {
        updateHeaderHeight();
      });
    };

    initialMeasurement();

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    resizeObserver.observe(headerContainerRef.current);
    const mutationObserver = new MutationObserver(() => {
      updateHeaderHeight();
    });

    mutationObserver.observe(headerContainerRef.current, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      updateHeaderHeight();
    });
  }, [navState.openNavName, headerData, userState.isLoading]);

  const toggleMenu = (menuName) => {
    updateHeaderHeight();

    setNavState((prevMenu) => {
      const newOpenNavName = prevMenu.openNavName === menuName ? '' : menuName;
      document.body.style.overflow = newOpenNavName ? 'hidden' : 'scroll';

      return {
        openNavName: newOpenNavName,
        activeMenu: '',
        nestedContent: null,
        activeNavItem: ''
      };
    });

    requestAnimationFrame(() => {
      updateHeaderHeight();
    });
  };

  const handleMenuClick = (item) => {
    setNavState((prevState) => ({
      ...prevState,
      activeMenu: 'nested',
      nestedContent: item,
      activeNavItem: item.title
    }));
  };

  const handleBackClick = () => {
    setNavState((prevState) => ({
      ...prevState,
      activeMenu: '',
      nestedContent: null,
      activeNavItem: ''
    }));
  };

  const fetchLocation = async (campusName) => {
    if (!campusName) return;

    try {
      const queryCampusName = campusName === 'Crossroads Church Online' ? 'Anywhere' : campusName;

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_CRDS_SITE_DOMAIN}api/getLocation?campusName=${encodeURIComponent(queryCampusName)}`
      );

      const {
        data: { serviceTimes: _serviceTimes, happenings }
      } = await response.json();

      setServiceTimes(_serviceTimes || []);
      setHappenings(happenings || []);
    } catch (error) {
      console.error('Error fetching happenings:', error);
    }
  };

  const getBackgroundClass = (title) => {
    const titleToClassMap = {
      'Come Visit': 'section--come-visit',
      'Watch, Listen, Read': 'section--watch-listen-read',
      'Get Connected': 'section--find-community',
      'Get Support': 'section--get-support',
      'GO | Change The World': 'section--go-change-the-world'
    };

    return titleToClassMap[title] || 'section--default';
  };

  const renderGlobalActions = () => (
    <div class="global-actions">
      <div
        class={`menu-container ${navState.openNavName === 'main-nav' ? 'nav-is-showing' : ''}`}
        data-automation-id="sh-menu"
        onClick={() => toggleMenu('main-nav')}
        data-label="menu"
      >
        <div
          class={navState.openNavName === 'main-nav' ? iconData.close.class : iconData.main.class}
          dangerouslySetInnerHTML={{
            __html:
              navState.openNavName === 'main-nav'
                ? iconData.close.innerHTML
                : iconData.main.innerHTML
          }}
        />
      </div>
      <div
        onClick={(e) => {
          e.preventDefault();
          setSearchModalOpen(true);
        }}
        class="search-container"
        data-automation-id="sh-search"
        aria-label="search"
        data-label="search"
      >
        <div
          class={iconData.search.class}
          dangerouslySetInnerHTML={{ __html: iconData.search.innerHTML }}
        />
      </div>
      <a href={`${rootURL}/watch`}>
        <div
          class="watch-container"
          data-automation-id="sh-watch"
          aria-label="watch"
          data-label="watch"
        >
          <div
            class={iconData.watch.class}
            dangerouslySetInnerHTML={{ __html: iconData.watch.innerHTML }}
          />
        </div>
      </a>
    </div>
  );

  const renderLogo = () => (
    <a
      href={rootURL}
      class="logo"
      data-automation-id="sh-logo"
      data-label="home"
      dangerouslySetInnerHTML={{ __html: iconData.logo.innerHTML }}
    />
  );

  const renderUserActions = () => {
    const { isAuthenticated, user } = userState;

    const profileIconClass =
      navState.openNavName === 'profile-nav' ? iconData.close.class : iconData.profile.class;
    const profileIconHTML =
      navState.openNavName === 'profile-nav'
        ? iconData.close.innerHTML
        : isAuthenticated
          ? `<div class="account-authenticated" style="background-image: url('${user?.user?.Photo?.Url || 'https://crossroads-media.imgix.net/images/avatar.svg'}');"/>`
          : iconData.profile.innerHTML;

    return (
      <div class="user-actions">
        {user?.campus?.Name && (
          <div
            ref={(el) => (dropdownRefs.current['my-site-nav'] = el)}
            class={`my-site-container ${navState.openNavName === 'my-site-nav' ? 'nav-is-showing' : ''}`}
            data-automation-id="sh-my-site"
            data-label={user?.campus?.Name}
            onClick={() => toggleMenu('my-site-nav')}
          >
            <div
              class={
                navState.openNavName === 'my-site-nav' ? iconData.close.class : iconData.site.class
              }
              dangerouslySetInnerHTML={{
                __html:
                  navState.openNavName === 'my-site-nav'
                    ? iconData.close.innerHTML
                    : iconData.site.innerHTML
              }}
            />
            {navState.openNavName === 'my-site-nav' && (
              <MySiteMenu
                isNavOpen={navState.openNavName === 'my-site-nav'}
                user={user}
                serviceTimes={serviceTimes}
                happenings={happenings}
                handleClose={() => toggleMenu('my-site-nav')}
                headerHeight={headerHeight}
              />
            )}
          </div>
        )}
        <a href={`${rootURL}/giving`}>
          <div class="give-container" data-label="give" data-automation-id="sh-give">
            <div
              class={iconData.give.class}
              dangerouslySetInnerHTML={{
                __html: iconData.give.innerHTML
              }}
            />
          </div>
        </a>
        {isAuthenticated ? (
          <div
            ref={(el) => (dropdownRefs.current['profile-nav'] = el)}
            className={`profile-container ${navState.openNavName === 'profile-nav' ? 'nav-is-showing' : ''}`}
            style={{ position: 'relative' }}
            data-label="My Account"
            data-automation-id="sh-profile"
            onClick={() => toggleMenu('profile-nav')}
          >
            {hasNewBadges && navState.openNavName !== 'profile-nav' && (
              <span
                style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: 'red',
                  borderRadius: '50%',
                  zIndex: '10'
                }}
              />
            )}
            <div
              className={profileIconClass}
              dangerouslySetInnerHTML={{ __html: profileIconHTML }}
            />
            {navState.openNavName === 'profile-nav' && (
              <MyAccountMenu
                data={headerData.profile}
                isNavOpen={navState.openNavName === 'profile-nav'}
                user={user}
                handleClose={() => toggleMenu('profile-nav')}
                headerHeight={headerHeight}
                hasNewBadges={hasNewBadges}
              />
            )}
          </div>
        ) : (
          <a
            href="#"
            className="profile-container"
            data-label="Sign In"
            data-automation-id="sh-sign-in"
            onClick={(e) => {
              e.preventDefault();
              AuthProvider.login();
            }}
          >
            <div
              className={profileIconClass}
              dangerouslySetInnerHTML={{ __html: profileIconHTML }}
            />
          </a>
        )}
      </div>
    );
  };

  if (userState.isLoading) {
    return null;
  }

  return (
    <ShadowWrapper styles={styles}>
      <div ref={headerContainerRef} className="header-container">
        {headerData && headerData.notifications && headerData.notifications.length > 0 && (
          <div className="alert-banner">
            <HeaderNotification
              data={headerData}
              user={userState.user}
              isAuthenticated={userState.isAuthenticated}
            />
          </div>
        )}
        <header className={navState.openNavName ? 'nav-is-showing' : ''}>
          <div className="global-nav-items">
            <div className="left-container">{renderGlobalActions()}</div>
            <div className="right-container">{renderUserActions()}</div>
          </div>
          <div className="logo-container">{renderLogo()}</div>
        </header>
      </div>
      <NavigationMenu
        navState={navState}
        headerData={headerData}
        handleMenuClick={handleMenuClick}
        handleBackClick={handleBackClick}
        getBackgroundClass={getBackgroundClass}
      />
      <SearchModal isOpen={isSearchModalOpen} onClose={() => setSearchModalOpen(false)} />
    </ShadowWrapper>
  );
};

export default SharedHeader;
